/**
 * Sehr dünner Fetch-Wrapper. Wirft bei !ok mit Server-Message.
 * Pfad immer absolut (z.B. "/api/v1/panels"); Vite-Proxy leitet an :4000.
 *
 * Optionaler Bearer-Token: wenn `livepuls.api.token` in localStorage liegt,
 * wird er als Authorization-Header mitgeschickt. Passt zu API_TOKEN im
 * Backend (siehe apps/backend/src/config/env.ts).
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const TOKEN_KEY = 'livepuls.api.token';

export function setApiToken(token: string): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getApiToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? '';
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = getApiToken();
  const res = await fetch(path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      message = body.error ?? body.message ?? message;
    } catch {
      /* leerer Body ist okay */
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function withBody(method: string, body?: unknown): RequestInit {
  return body === undefined
    ? { method }
    : { method, body: JSON.stringify(body) };
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, withBody('POST', body)),
  put: <T>(path: string, body?: unknown) => request<T>(path, withBody('PUT', body)),
  delete: <T = void>(path: string) => request<T>(path, { method: 'DELETE' }),
};
